import { useMemo, useEffect } from "react";
import useResourceLoader from '../../../util/useResourceLoader';
import fetchApi from "../../../util/fetchApi";
import checkErrors from "../../../api/checkErrors";
import { useSideChannelSubscription } from "../../../util/useSideChannel";
import { getObject } from "../../../util/mapObject";
import Jnx, { useJnx, useMultipleJnx } from "../../../util/jnx";

function useOptionsLookup({
    lookup,
    rootFormData,
    formContext
}) {
    const {
        api: propApi,
        entity,
        jnx: apiResponseJnxExpr,
        params,
        options: fetchOptions,
        requiredParams = false,
    } = lookup || {};

    const apiResponseJnx = useJnx(apiResponseJnxExpr);

    const paramsJnx = useMultipleJnx(params);

    const paramsString = useMemo(() => computeParamString(rootFormData, params, paramsJnx, {
        root: rootFormData,
        formContext: formContext || {}
    }), [rootFormData, params, paramsJnx, formContext]);

    const [options, loadingOptions, errorLoadingOptions] = useResourceLoader(async () => loadOptionsData(propApi, apiResponseJnx, paramsString, fetchOptions, entity, requiredParams), [propApi, apiResponseJnx, paramsString, fetchOptions, entity]);

    return {
        options,
        loadingOptions,
        errorLoadingOptions
    }
}

export function lookupOptions(lookup, rootFormData) {
    const {
        api: propApi,
        entity,
        jnx: apiResponseJnxExpr,
        params,
        options: fetchOptions,
        requiredParams = false,
    } = lookup || {};

    const apiResponseJnx = apiResponseJnxExpr ? new Jnx(apiResponseJnxExpr) : null;

    const paramsString = computeParamString(rootFormData, params, undefined, {
        root: rootFormData,
        formContext: undefined
    });

    return loadOptionsData(propApi, apiResponseJnx, paramsString, fetchOptions, entity, requiredParams);
}

function computeApiUrl(api, paramsString) {
    let module = null;
    let listFn = null;

    let m = /^(api|entity):(.+)$/.exec(api);
    if (m) {
        if (m[1] === 'entity') {
            listFn = (x) => x.items;
        }
        module = "api";
        api = m[1] === 'entity' ? `uwe-entities/${m[2]}/list` : m[2];
    } else {
        m = /^https?:\/\/(.+)$/.exec(api);
        if (m) {
            module = 'external';
        } else {
            module = 'lookup';
        }
    }

    const delimiter = api.indexOf('?') > -1 ? '&' : '?';
    const url = `${api}${paramsString ? `${delimiter}${paramsString}` : ''}`;

    return [module, url, listFn];
}


export function computeLookupParams(rootFormData, params, paramsJnx, bindings) {
    if (!params || typeof params !== 'object') {
        return [];
    }

    if (!paramsJnx) {
        paramsJnx = {}
    }

    const paramsList = Object.entries(params).map(([param, value]) => {
        if (param.toString().startsWith("expr:")) {
            const cleanedParam = param.replace(/^expr:/, "");

            if (paramsJnx[cleanedParam]) {
                value = paramsJnx[cleanedParam].eval(rootFormData || {}, '', bindings);
            }
            param = cleanedParam;
        }

        if (value && value.field){
            value = rootFormData ? getObject(rootFormData, value.field) : value.default || "";
        }
  
        return [param, `${value}`];
    });
    
    return  paramsList.length ? (
        paramsList.filter(([_, v]) => !!v)
    ) : (
        []
    );
}


function computeParamString(rootFormData, params, paramsJnx, bindings) {
    return rootFormData || (!!params)
        ? encodeKeyValuePairs(computeLookupParams(rootFormData, params, paramsJnx, bindings))
        : "";
}

function encodeKeyValuePairs(kvPairs) {
    return kvPairs?.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&") ?? "";
}

async function loadOptionsData(propApi, apiResponseJnx, paramsString, fetchOptions, entity, requiredParams) {
    if(requiredParams && paramsString?.length === 0) return;

    const api = entity ? `entity:${entity}` : propApi;
    if (!api) return null;
    const [module, url, listFn] = computeApiUrl(api, paramsString);
    let data = await checkErrors(await fetchApi[module].get(url, fetchOptions));
    if (listFn) {
        data = listFn(data);
    }
    if (apiResponseJnx) {
        data = apiResponseJnx.eval(data, '', { data });
    }
    return data ? (Array.isArray(data) ? data : [data]) : null;
}



export default useOptionsLookup;